import {
  experienceTypeToTestTypeKey,
  TEST_TYPE_OPTIONS,
} from "@intelligems/ig-utils/support-ticket";
import { useState } from "react";
import { toast } from "react-toastify";

import { IgDialog } from "@intelligems/ig-theme/IgDialog";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import Stack from "@mui/material/Stack";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { usePostSupportTicket } from "@src/api/support/post-support-ticket";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { buildEarlyTestTicketPayload } from "@src/features/experiments/utils/earlyTestTicket";
import { useDialog } from "@src/hooks/dialog";
import {
  mpSupportTicketSubmissionFailed,
  mpSupportTicketSubmitted,
} from "@src/utils/mixpanel";

/**
 * Opens after a test is successfully ended or paused before reaching
 * significance, offering to file a pre-populated support ticket. Triggered from
 * the end/pause mutation success handlers (see useEndExperience /
 * usePauseExperience) so it only ever appears once the action has succeeded.
 */
export const EarlyTestReportDialog = () => {
  const dialog = useDialog("earlyTestReport");
  const { data: userData } = useLoggedInUser();
  const { data: orgData } = useGetOrg();
  const { mutate: submitTicket, isPending } = usePostSupportTicket();
  const [note, setNote] = useState("");

  const user = userData?.user;
  const org = orgData?.org;
  const props = dialog.props;

  const handleClose = () => {
    setNote("");
    dialog.close();
  };

  const handleSend = () => {
    if (!props || !user?.email || !org) {
      return;
    }

    const payload = buildEarlyTestTicketPayload({
      experience: props.experience,
      org,
      user,
      action: props.action,
      daysLive: props.daysLive,
      origin: window.location.origin,
      note,
    });

    submitTicket(payload, {
      onSuccess: () => {
        // ref_path tag so early-test reports are distinguishable from form tickets.
        mpSupportTicketSubmitted(
          TEST_TYPE_OPTIONS[experienceTypeToTestTypeKey(props.experience.type)],
          "early_test_report"
        );
        toast.success("Reported to support — we'll follow up.");
        handleClose();
      },
      onError: () => {
        mpSupportTicketSubmissionFailed(
          TEST_TYPE_OPTIONS[experienceTypeToTestTypeKey(props.experience.type)]
        );
        toast.error("Failed to report the issue. Please try again.");
      },
    });
  };

  const actionWord = props?.action === "pause" ? "paused" : "ended";
  const category = props?.experience.category;

  return (
    <IgDialog
      showDialog={dialog.isOpen}
      handleClose={handleClose}
      title={
        category
          ? `Having an issue with this ${officialCategoryName(category, false)}?`
          : ""
      }
      rawContent={
        <Stack spacing={2} textAlign="start" sx={{ pt: 1 }}>
          <IgTypography>
            You {actionWord}{" "}
            <IgTypography fontWeight={600}>
              {props?.experience.name}
            </IgTypography>{" "}
            before it had much time to run. If something went wrong, send the
            details to our support team so we can investigate — even if you
            delete the {category && officialCategoryName(category, false)}{" "}
            later.
          </IgTypography>
          <IgTextField
            label="Anything else? (optional)"
            fullWidth
            multiline
            minRows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Stack>
      }
      handleYesClick={handleSend}
      handleNoClick={handleClose}
      yesButtonText="Send report"
      noButtonText="Not now"
      yesButtonDisabled={isPending || !user?.email || !org}
      showCloseIcon
      dontUseDialogContent
      dialogHeight={null}
    />
  );
};
